import type { IPackageConfig } from "lib/types/global";
import { logAdapterWarn } from "./logger";

type PackKeys = Partial<Pick<IPackageConfig, "id" | "billingProductCode" | "epochPackageName" | "type">>;

const HELPER_NAME = "ChannelPackMembership";

// The service snake-cases "MyHome Team" to ATT_TV_MYHOME_TEAM; package-config's id is ATT_TV_MYHOMETEAM.
// TODO: drop this alias once package-config authors a billing code for MyHome Team.
const SERVICE_ID_ALIASES: Record<string, string> = { ATT_TV_MYHOMETEAM: "ATT_TV_MYHOME_TEAM" };

/** Any package-config type starting with "genre" ("genre", "genre-mini" today) is a genre pack — prefix match on purpose. */
export const isGenreType = (type?: string): boolean => String(type ?? "").startsWith("genre");

/** A row is inactive only when it says so explicitly (boolean false, or the string "false"). */
export const isInactiveChannel = (channel: any): boolean => String(channel?.IsActive) === "false";

/**
 * WAT-12389: the ONE "is this channel in this pack" check every channel listing shares.
 *
 * - Inactive rows are never members. Only an explicit `false` (boolean or the string a
 *   Storyblok feed row can carry) counts as inactive, so a source without the field is
 *   not emptied. Some callers already drop rows with a missing IsActive before calling
 *   (`filterChannels`, `groupChannelsByGenrePackage`); that stricter pre-filter is theirs.
 * - With a package-config ARRAY, the first entry whose id matches is used. Callers that
 *   concatenate configs (package compare: base, then on-demand) get the base entry
 *   when ids collide.
 * - Membership is tried in this order: billing product code, then the Epoch package name
 *   (channels-service rows carry the raw name, e.g. "MyHome Team", which has no billing code
 *   in package-config), then the pack id itself, then the service's alias for that id
 *   (ATT_TV_MYHOME_TEAM for ATT_TV_MYHOMETEAM), which needs no package-config.
 * - Only the included list is read by default. The /channel-lineup/ page (the parser and
 *   filterChannels' genre arm) passes `addonAndPromo` to keep its legacy behaviour (an
 *   add-on/promo hit marks the genre indicator IN); no other listing should.
 *
 * `packageConfig` is the pack's own entry, or the whole package-config list to look it up in.
 * A single entry whose `id` names a different pack is ignored.
 */
export const isChannelInPack = (
  channel: any,
  packageId: string,
  packageConfig?: PackKeys | PackKeys[],
  { addonAndPromo = false }: { addonAndPromo?: boolean } = {}
): boolean => {
  if (isInactiveChannel(channel)) {
    return false;
  }
  const config = Array.isArray(packageConfig) ?
    packageConfig?.find?.(pkg => pkg?.id === packageId) :
    (packageConfig?.id && packageConfig?.id !== packageId ? undefined : packageConfig);
  const lists = (addonAndPromo ?
    [channel?.packageListSystem, channel?.addonPackageList, channel?.promoPackageList] :
    [channel?.packageListSystem]).filter(Array.isArray);
  const inAnyList = (key?: string) => Boolean(key) && lists.some(list => list?.includes?.(key));
  return (
    inAnyList(config?.billingProductCode) ||
    inAnyList(config?.epochPackageName) ||
    inAnyList(packageId) ||
    inAnyList(SERVICE_ID_ALIASES?.[packageId])
  );
};

/**
 * Adds each genre-pack id the shared check resolves from a billing code or Epoch name (e.g.
 * "MyHome Team" to ATT_TV_MYHOMETEAM) to the included list of every row, for listings whose
 * column logic matches exact ids. A row with nothing to add is returned unchanged.
 */
export const withGenrePackIds = (channels: any[], packageConfig?: PackKeys[]): any[] => {
  const genreConfigs = packageConfig?.filter?.(pkg => isGenreType(pkg?.type)) ?? [];
  return channels?.map?.(channel => {
    const ids = genreConfigs
      .filter(pkg => !channel?.packageListSystem?.includes?.(pkg?.id) && isChannelInPack(channel, pkg?.id, pkg))
      .map(pkg => pkg?.id);
    return ids.length ? {
      ...channel,
      packageListSystem: [...(channel?.packageListSystem ?? []), ...ids]
    } : channel;
  }) ?? [];
};

// Warned (pack id + feed lastUpdated) pairs, so a read-path call warns once per feed refresh, not per request.
const warnedZeroMatch = new Set<string>();

/**
 * `withGenrePackIds` over a channels-service envelope (`{ platform, lastUpdated, channels }`); absent stays absent.
 *
 * WAT-12389: channels-service serves the MyHome Team out-of-market RSNs and names the pack by its
 * Epoch name, so the populator and every repository read path run this on the OTT envelope; all
 * downstream exact-id checks (agent tool, lineup client, compare tools) then see the package-config
 * id. OTT only: package-config genre packs are streaming packs, so satellite stays raw. This covers
 * channels-service rows only; AEM-fallback rows get the id only where an adapter re-runs
 * `withGenrePackIds` (the channel compare tool), so the agent tool's AEM path is NOT covered here.
 *
 * Warns when a genre pack resolves 0 rows from a non-empty envelope (a renamed Epoch package or an
 * edited package-config would otherwise empty that pack's listings silently).
 */
export const withGenrePackIdsOnEnvelope = (envelope: any, packageConfig?: PackKeys[]) => {
  if (!envelope) {
    return envelope;
  }
  const channels = withGenrePackIds(envelope?.channels ?? [], packageConfig);
  if (channels?.length) {
    packageConfig?.filter?.(pkg => isGenreType(pkg?.type))?.forEach?.(pkg => {
      // No lastUpdated: key on the row count so a changed feed can warn again.
      const warnKey = `${pkg?.id}|${envelope?.lastUpdated ?? channels?.length}`;
      if (!warnedZeroMatch.has(warnKey) && !channels?.some?.(c => !isInactiveChannel(c) && c?.packageListSystem?.includes?.(pkg?.id))) {
        if (warnedZeroMatch.size > 500) {
          warnedZeroMatch.clear();
        }
        warnedZeroMatch.add(warnKey);
        logAdapterWarn?.(HELPER_NAME, `genre pack ${pkg?.id} resolved 0 channels-service rows`, {
          packageId: pkg?.id,
          epochPackageName: pkg?.epochPackageName,
          rows: channels?.length
        });
      }
    });
  }
  return {
    ...envelope,
    channels
  };
};
